import { useState } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { Field, Callout, OutputPanel, LoadRow } from '../UI';
import { fmtMoney, fmtDate, today } from '../../utils/constants';

export default function HandoffTab({
  contract, updateC, s215, profile, apiKey, callAI, toast, onCelebrate,
}) {
  const [step, setStep] = useState(contract.handoff?.step || 1);
  const [internalDraft, setInternalDraft] = useState(contract.handoff?.internalDraft || '');
  const [contractorDraft, setContractorDraft] = useState(contract.handoff?.contractorDraft || '');
  const [internalLoading, setInternalLoading] = useState(false);
  const [contractorLoading, setContractorLoading] = useState(false);
  const [internalEmail, setInternalEmail] = useState(contract.handoff?.internalEmail || { cm: '', accounting: '', supervisor: '' });
  const [contractorEmail, setContractorEmail] = useState(contract.handoff?.contractorEmail || '');
  const [generating, setGenerating] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const persist = (updates) => updateC({ handoff: { ...(contract.handoff || {}), ...updates } });

  // Build contract context for Penny
  const ctx = [
    'Contract Title: ' + (contract.title || 'TBD'),
    'Agreement No.: ' + (s215?.agreementNum || 'TBD'),
    'Contractor: ' + (s215?.contractorName || 'TBD'),
    'Budget: ' + fmtMoney(contract.budget),
    'Term: ' + (s215?.startDate || 'TBD') + ' through ' + (s215?.endDate || 'TBD'),
    'Type: ' + (contract.isIT === 'yes' ? 'IT Services — SCM Vol 2' : 'Non-IT Services — SCM Vol 1'),
    'Method: ' + (contract.procMethod || 'TBD'),
    'Category: ' + (contract.category || 'Professional Services'),
    'DGS/OLS Routed: ' + (s215?.dgsRouted ? 'Yes — ' + (s215?.dgsRoutedDate || '') : 'No'),
    'DGS/OLS Approved: ' + (s215?.dgsApproved ? 'Yes — ' + (s215?.dgsApprovedDate || '') : 'No'),
    'Analyst: ' + (profile?.contactName || 'TBD'),
    'Division: ' + (profile?.agencyDivision || 'Admin Services Division'),
  ].join('\n');

  async function draftInternal() {
    setInternalLoading(true);
    try {
      const sys = `You are Penny, the GoldenScope contracting assistant. Draft a professional internal handoff package for a California state contract that has been fully executed. 
      
      The package should include three sections:
      
      1. CONTRACT MANAGER BRIEFING
      Full contract overview, key dates, deliverable schedule, payment terms, contractor contact info, exhibits summary, and any special terms or conditions to monitor.
      
      2. ACCOUNTING NOTICE
      Agreement number, billing codes, payment schedule, encumbrance details, invoice routing instructions, and net-30 payment terms reminder.
      
      3. SUPERVISOR SUMMARY
      Procurement method used, DGS routing status, any exemptions or special circumstances, timeline from intake to execution, and any outstanding items.
      
      Use formal California state government language. Number each section. No markdown.`;

      const text = await callAI(apiKey, {
        system: sys,
        model: 'claude-sonnet-4-6',
        max_tokens: 2000,
        messages: [{ role: 'user', content: 'Contract details:\n' + ctx + '\n\nDraft the internal handoff package.' }],
        tag: 'internal_briefing',
        contract_id: contract.id,
      });
      setInternalDraft(text);
      persist({ internalDraft: text });
    } catch (e) { toast.error('Draft failed: ' + e.message); }
    finally { setInternalLoading(false); }
  }

  async function draftContractor() {
    setContractorLoading(true);
    try {
      const sys = `You are Penny, the GoldenScope contracting assistant. Draft a professional Notice of Contract Execution letter to the contractor for a California state contract.
      
      Include:
      1. Official notice that the contract is fully executed and effective
      2. Contract start date and end date
      3. First deliverable due date (if known) 
      4. Invoice submission instructions and billing contact
      5. Contract Manager point of contact (name, phone, email)
      6. Any important terms or conditions the contractor should be aware of
      
      Use formal, professional language appropriate for an official state government notice. No markdown. Use [PLACEHOLDER] for any unknown fields.`;

      const text = await callAI(apiKey, {
        system: sys,
        model: 'claude-sonnet-4-6',
        max_tokens: 1000,
        messages: [{ role: 'user', content: 'Contract details:\n' + ctx + '\n\nDraft the contractor execution notice.' }],
        tag: 'contractor_notice',
        contract_id: contract.id,
      });
      setContractorDraft(text);
      persist({ contractorDraft: text });
    } catch (e) { toast.error('Draft failed: ' + e.message); }
    finally { setContractorLoading(false); }
  }

  async function generateSurveyLinks() {
    setGenerating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate internal survey token
      const { data: internalToken, error: e1 } = await supabase
        .from('survey_tokens')
        .insert({
          contract_id: contract.id,
          contract_title: contract.title,
          analyst_id: user.id,
          analyst_name: profile?.contactName || 'Contract Analyst',
          recipient_type: 'internal',
          recipient_name: 'Internal Stakeholders',
        })
        .select('token')
        .single();

      if (e1) throw e1;

      // Generate contractor survey token
      const { data: contractorToken, error: e2 } = await supabase
        .from('survey_tokens')
        .insert({
          contract_id: contract.id,
          contract_title: contract.title,
          analyst_id: user.id,
          analyst_name: profile?.contactName || 'Contract Analyst',
          recipient_type: 'contractor',
          recipient_name: s215?.contractorName || 'Contractor',
        })
        .select('token')
        .single();

      if (e2) throw e2;

      const base = window.location.origin;
      const internalLink = `${base}/survey?token=${internalToken.token}`;
      const contractorLink = `${base}/survey?token=${contractorToken.token}`;

      persist({
        internalSurveyLink: internalLink,
        contractorSurveyLink: contractorLink,
        surveyGeneratedAt: new Date().toISOString(),
      });

      toast.success('Survey links generated — valid for 14 days');
    } catch (e) { toast.error('Failed to generate links: ' + e.message); }
    finally { setGenerating(false); }
  }

  async function archiveContract() {
    setArchiving(true);
    try {
      updateC({
        archived: true,
        archivedAt: today(),
        handoff: { ...(contract.handoff || {}), completedAt: today(), step: 3 },
      });
      // Trigger celebration after brief delay so archive saves first
      setTimeout(() => { if (onCelebrate) onCelebrate(); }, 800);
    } catch (e) { toast.error('Archive failed: ' + e.message); }
    finally { setArchiving(false); }
  }

  const internalLink = contract.handoff?.internalSurveyLink;
  const contractorLink = contract.handoff?.contractorSurveyLink;

  return (
    <div>
      {/* Step tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          [1, 'Step 1', 'Internal Handoff'],
          [2, 'Step 2', 'Contractor Notice'],
          [3, 'Step 3', 'Archive'],
        ].map(([n, num, label]) => (
          <div
            key={n}
            onClick={() => { setStep(n); persist({ step: n }); }}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 'var(--r-lg)',
              border: '1.5px solid ' + (step === n ? 'var(--gold-500)' : 'var(--slate-200)'),
              background: step === n ? 'var(--navy-900)' : 'var(--white)',
              cursor: 'pointer', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: step === n ? 'var(--gold-400)' : 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{num}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: step === n ? 'var(--white)' : 'var(--navy-700)', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Step 1 — Internal Handoff */}
      {step === 1 && (
        <>
          <div className="card">
            <h3 className="card-title">✦ Internal Handoff Package · Penny</h3>
            <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 16 }}>
              Penny drafts a comprehensive handoff package for your Contract Manager, Accounting office, and Supervisor.
            </p>
            <button className="btn btn-gold" disabled={internalLoading} onClick={draftInternal}>
              {internalLoading ? <><div className="spin spin-sm" /> Drafting…</> : '✦ Draft Internal Handoff Package'}
            </button>
            {internalLoading && <LoadRow msg="Penny is drafting the internal handoff package…" />}
            <OutputPanel label="Internal Handoff Package" content={internalDraft} />
          </div>

          {internalDraft && (
            <div className="card">
              <h3 className="card-title">📧 Internal Handoff Email</h3>
              <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 16 }}>
                Enter recipient emails then copy the handoff package above into your email client.
              </p>
              <div className="form-grid">
                <Field label="Contract Manager Email" value={internalEmail.cm} onChange={v => { setInternalEmail(p => ({ ...p, cm: v })); persist({ internalEmail: { ...internalEmail, cm: v } }); }} placeholder="cm@energysafety.ca.gov" />
                <Field label="Accounting Email" value={internalEmail.accounting} onChange={v => { setInternalEmail(p => ({ ...p, accounting: v })); persist({ internalEmail: { ...internalEmail, accounting: v } }); }} placeholder="accounting@energysafety.ca.gov" />
                <Field label="Supervisor Email" value={internalEmail.supervisor} onChange={v => { setInternalEmail(p => ({ ...p, supervisor: v })); persist({ internalEmail: { ...internalEmail, supervisor: v } }); }} placeholder="supervisor@energysafety.ca.gov" />
              </div>

              {/* Survey link */}
              {!internalLink ? (
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} disabled={generating} onClick={generateSurveyLinks}>
                  {generating ? <><div className="spin spin-sm" /> Generating…</> : '🔗 Generate Survey Links'}
                </button>
              ) : (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', borderRadius: 'var(--r-md)' }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--teal-700)', marginBottom: 4 }}>✓ Survey Links Generated (valid 14 days)</div>
                  <div style={{ fontSize: 12, color: 'var(--slate-600)', wordBreak: 'break-all' }}>Internal: {internalLink}</div>
                </div>
              )}

              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={() => setStep(2)}>
                  Continue to Step 2 →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Step 2 — Contractor Notice */}
      {step === 2 && (
        <>
          <div className="card">
            <h3 className="card-title">✦ Contractor Execution Notice · Penny</h3>
            <p style={{ fontSize: 12.5, color: 'var(--slate-500)', marginBottom: 16 }}>
              Penny drafts an official Notice of Contract Execution for the contractor.
            </p>
            <button className="btn btn-gold" disabled={contractorLoading} onClick={draftContractor}>
              {contractorLoading ? <><div className="spin spin-sm" /> Drafting…</> : '✦ Draft Contractor Notice'}
            </button>
            {contractorLoading && <LoadRow msg="Penny is drafting the contractor notice…" />}
            <OutputPanel label="Contractor Execution Notice" content={contractorDraft} />
          </div>

          {contractorDraft && (
            <div className="card">
              <h3 className="card-title">📧 Contractor Email</h3>
              <div className="form-grid">
                <Field
                  label="Contractor Email"
                  value={contractorEmail}
                  onChange={v => { setContractorEmail(v); persist({ contractorEmail: v }); }}
                  placeholder="contractor@company.com"
                  className="span-full"
                />
              </div>

              {contractorLink && (
                <div style={{ marginTop: 12, padding: '10px 14px', background: 'var(--teal-50)', border: '1.5px solid var(--teal-200)', borderRadius: 'var(--r-md)' }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--teal-700)', marginBottom: 4 }}>✓ Contractor Survey Link (valid 14 days)</div>
                  <div style={{ fontSize: 12, color: 'var(--slate-600)', wordBreak: 'break-all' }}>{contractorLink}</div>
                </div>
              )}

              {!contractorLink && (
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} disabled={generating} onClick={generateSurveyLinks}>
                  {generating ? <><div className="spin spin-sm" /> Generating…</> : '🔗 Generate Survey Links'}
                </button>
              )}

              <div style={{ marginTop: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={() => setStep(3)}>
                  Continue to Step 3 — Archive →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Step 3 — Archive */}
      {step === 3 && (
        <div className="card">
          <h3 className="card-title">Archive Contract</h3>
          {contract.archived ? (
            <Callout type="teal">
              ✓ This contract was archived on {contract.archivedAt}. It is now read-only and visible in the Archive section.
            </Callout>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--slate-600)', marginBottom: 16, lineHeight: 1.6 }}>
                Archiving will mark this contract as complete and move it to the Archive section. The file will be read-only and no further edits can be made. Confirm that both handoff packages have been sent before archiving.
              </p>
              <div style={{ marginBottom: 16 }}>
                {[
                  ['Internal handoff package drafted and sent', !!internalDraft],
                  ['Contractor notice drafted and sent', !!contractorDraft],
                  ['Survey links generated and included in emails', !!(internalLink && contractorLink)],
                ].map(([label, done]) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 16, color: done ? 'var(--green-500)' : 'var(--slate-300)' }}>{done ? '✓' : '○'}</span>
                    <span style={{ fontSize: 13, color: done ? 'var(--slate-700)' : 'var(--slate-400)' }}>{label}</span>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-danger"
                disabled={archiving}
                onClick={archiveContract}
              >
                {archiving ? <><div className="spin spin-sm" /> Archiving…</> : '📁 Archive Contract'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
